import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { fetchStores } from '../../api/stores';
import { API_BASE_URL } from '../../config';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_STORE_NAME_MATCH = 'main (tagum)';

function getStoreId(store) {
  return store?._id || store?.id || '';
}

function pickDefaultStore(list) {
  if (!Array.isArray(list) || list.length === 0) return '';
  const preferred = list.find((store) => String(store?.name || '').toLowerCase().includes(DEFAULT_STORE_NAME_MATCH));
  return getStoreId(preferred || list[0]);
}

export default function AdminDashboard({ authConfig, backendUrl, onUnauthorized }) {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const loadStores = useCallback(async () => {
    try {
      const list = await fetchStores();
      setStores(Array.isArray(list) ? list : []);
      if (!storeId && list?.length) setStoreId(pickDefaultStore(list));
    } catch {
      setStores([]);
    }
  }, [storeId]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { year, from, to };
      if (storeId) params.storeId = storeId;
      const res = await axios.get(`${backendUrl || API_BASE_URL}/api/reports/dashboard`, {
        params,
        ...authConfig
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [authConfig, backendUrl, year, from, to, storeId, onUnauthorized]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const maxMonthly = data?.monthlySales?.length
    ? Math.max(...data.monthlySales.map((m) => m.total), 1)
    : 1;

  if (loading && !data) {
    return <p className="text-gray-600">Loading dashboard...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <h2 className="text-xl font-semibold">Management • Dashboard</h2>
        <select
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
          aria-label="Store"
        >
          <option value="">All stores</option>
          {stores.map((s) => (
            <option key={s._id || s.id} value={s._id || s.id}>{s.name || 'Store'}</option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="border rounded px-2 py-1 text-sm"
        >
          {[new Date().getFullYear(), new Date().getFullYear() - 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {error && <p className="text-red-600">{error}</p>}

      {/* Monthly Sales */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-2">Monthly Sales - {data?.year || year}</h3>
        <p className="text-sm text-gray-500 mb-4">Sales data grouped by month</p>
        <div className="flex items-end gap-1 h-48">
          {(data?.monthlySales || Array(12).fill(0).map((_, i) => ({ month: i + 1, monthName: MONTH_NAMES[i], total: 0 }))).map((m, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full bg-indigo-600 rounded-t min-h-[4px] transition-all"
                style={{ height: `${Math.max(2, (m.total / maxMonthly) * 100)}%` }}
                title={`${m.monthName}: P${Number(m.total).toLocaleString()}`}
              />
              <span className="text-xs text-gray-500 truncate w-full text-center">
                {String(m.monthName || '').slice(0, 3)}
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
          {data?.monthlySales?.map((m, i) => (
            m.total > 0 && (
              <span key={i}>{m.monthName}: P{Number(m.total).toLocaleString()}</span>
            )
          ))}
        </div>
      </div>

      {/* Total Sales + Top Month */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Total Sales</p>
          <p className="text-3xl font-bold">P{(Number(data?.totalSales || 0) / 1000).toFixed(2)}K</p>
          {data?.topPerformingMonth && (
            <p className="text-sm text-gray-600 mt-2">
              Top performing month: <strong>{data.topPerformingMonth.monthName}</strong> — P{Number(data.topPerformingMonth.total).toLocaleString()}
            </p>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Today&apos;s sales</p>
          <p className="text-2xl font-bold">P{Number(data?.todaySales || 0).toFixed(2)}</p>
          <p className="text-sm text-gray-600">{data?.todayOrderCount || 0} orders today</p>
          <p className="text-sm text-amber-600 mt-2">{data?.lowStockCount || 0} low stock items</p>
        </div>
      </div>

      {/* Periodic Reports */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold mb-4">Periodic Reports ({from} - {to})</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() => loadDashboard()}
            className="bg-indigo-600 text-white px-4 py-1 rounded text-sm hover:bg-indigo-500"
          >
            Apply
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded p-4">
            <h4 className="font-medium mb-2">Top Products</h4>
            {data?.periodic?.topProducts?.length ? (
              <ul className="text-sm space-y-1">
                {data.periodic.topProducts.slice(0, 5).map((p, i) => (
                  <li key={i}>{p.name} — P{Number(p.revenue || 0).toLocaleString()}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No data to display</p>
            )}
          </div>
          <div className="border rounded p-4">
            <h4 className="font-medium mb-2">Hourly Sales</h4>
            {data?.periodic?.hourlySales?.some((h) => h.total > 0) ? (
              <div className="space-y-1 text-sm">
                {data.periodic.hourlySales.filter((h) => h.total > 0).slice(0, 5).map((h) => (
                  <p key={h.hour}>{h.hour}:00 — P{Number(h.total).toLocaleString()}</p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No data to display</p>
            )}
          </div>
          <div className="border rounded p-4">
            <h4 className="font-medium mb-2">Total Sales (Amount)</h4>
            <p className="text-2xl font-bold">P{Number(data?.periodic?.totalSalesAmount || 0).toLocaleString()}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="border rounded p-4">
            <h4 className="font-medium mb-2">Top Product Groups</h4>
            <p className="text-xs text-gray-500 mb-2">Top selling product groups in selected period</p>
            {data?.periodic?.topProductGroups?.length ? (
              <ul className="text-sm space-y-1">
                {data.periodic.topProductGroups.map((g, i) => (
                  <li key={i}>{g.category} — P{Number(g.revenue || 0).toLocaleString()}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No data to display</p>
            )}
          </div>
          <div className="border rounded p-4">
            <h4 className="font-medium mb-2">Top Customers</h4>
            <p className="text-xs text-gray-500 mb-2">Lead customers in selected period (top 5)</p>
            {data?.periodic?.topCustomers?.length ? (
              <ul className="text-sm space-y-1">
                {data.periodic.topCustomers.map((c, i) => (
                  <li key={i}>{c.customerName || c.customerPhone} — P{Number(c.total || 0).toLocaleString()}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No data to display</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
